import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api';

export const clientFilesKeys = {
  folders: (clientId) => ['clientFolders', clientId],
  /** Root list modes: `all` (default root), `inbox` (document-request + client uploads queue), `archived`. */
  contents: (clientId, folderId, rootMode = 'all') => ['clientFileContents', clientId, folderId ?? 'root', rootMode],
};

function normalizeFile(f) {
  if (!f) return f;
  return {
    ...f,
    file_url: f.file_url || f.url || null,
  };
}

export function useClientFoldersQuery(clientId) {
  return useQuery({
    queryKey: clientFilesKeys.folders(clientId),
    enabled: Boolean(clientId),
    queryFn: async () => {
      const { data } = await api.get(`/api/multi-tenant/clients/${clientId}/folders/`);
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useClientFileContentsQuery(clientId, folderId, rootMode = 'all', options = {}) {
  const { enabled: enabledIn, ...queryOptions } = options;
  const effectiveMode = folderId ? 'all' : rootMode;
  const includeArchived = effectiveMode === 'archived';
  return useQuery({
    ...queryOptions,
    queryKey: clientFilesKeys.contents(clientId, folderId, effectiveMode),
    enabled: enabledIn !== undefined ? Boolean(clientId) && enabledIn : Boolean(clientId),
    queryFn: async () => {
      if (folderId) {
        const { data } = await api.get(`/api/multi-tenant/clients/${clientId}/folders/${folderId}/contents/`, {
          params: { include_archived: includeArchived ? 'true' : 'false' },
        });
        return {
          subfolders: Array.isArray(data.folders) ? data.folders : [],
          files: (data.files || []).map(normalizeFile),
        };
      }
      const { data } = await api.get(`/api/multi-tenant/clients/${clientId}/files/`, {
        params: { include_archived: includeArchived ? 'true' : 'false' },
      });
      let files = (data.files || []).map(normalizeFile);
      if (effectiveMode === 'inbox') {
        files = files.filter((f) => f.source === 'document_request');
      }
      return {
        subfolders: [],
        files,
      };
    },
  });
}

export function useRenameDocumentMutation(clientId, onToast) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ fileId, fileName }) => {
      const idStr = String(fileId);
      if (idStr.startsWith('dr_')) {
        throw new Error('Rename is not available for client-uploaded files.');
      }
      const { data } = await api.patch(`/api/multi-tenant/clients/${clientId}/files/${fileId}/rename/`, {
        file_name: fileName,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'clientFileContents' && q.queryKey[1] === clientId });
      qc.invalidateQueries({ queryKey: clientFilesKeys.folders(clientId) });
      onToast?.('File renamed', 'success');
    },
    onError: (err) => {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.message || 'Rename failed';
      onToast?.(msg, 'error');
    },
  });
}

export function useDeleteDocumentMutation(clientId, onToast) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file }) => {
      const idStr = String(file.id);
      if (idStr.startsWith('dr_')) {
        await api.patch(`/api/multi-tenant/clients/${clientId}/files/${file.id}/`, { is_archived: true });
      } else {
        await api.delete(`/api/multi-tenant/clients/${clientId}/files/${file.id}/delete/`);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clientFilesKeys.folders(clientId) });
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'clientFileContents' && q.queryKey[1] === clientId });
      onToast?.('File deleted', 'success');
    },
    onError: (err) => {
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Delete failed';
      onToast?.(msg, 'error');
    },
  });
}

export function useShareDocumentMutation(clientId, onToast) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, emailRecipients, message }) => {
      await api.post(`/api/multi-tenant/clients/${clientId}/files/${file.id}/share/`, {
        email_recipients: emailRecipients,
        message: message || '',
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'clientFileContents' && q.queryKey[1] === clientId });
      onToast?.('Shared with client', 'success');
    },
    onError: (err) => {
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Share failed';
      onToast?.(msg, 'error');
    },
  });
}

export function useSignDocumentMutation(clientId, onToast) {
  return useMutation({
    mutationFn: async ({ file }) => {
      const res = await api.post('/api/esignatures/create-from-file/', {
        source_file_id: file.id,
        client_id: clientId,
        document_name: file.file_name,
      });
      return res.data;
    },
    onSuccess: (data) => {
      const url = data?.signing_url || data?.redirect_url || data?.zoho_sign_url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        onToast?.('Opening e-signature', 'success');
      }
      /* Draft-only flow: UI (e.g. Files tab) opens place-fields from mutate onSuccess + toast there. */
    },
    onError: (err) => {
      const msg = err.response?.data?.detail || err.response?.data?.error || 'E-sign failed';
      onToast?.(msg, 'error');
    },
  });
}

export function useMoveDocumentMutation(clientId, onToast) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ fileId, folderId }) => {
      const idStr = String(fileId);
      if (idStr.startsWith('dr_')) {
        await api.post('/api/multi-tenant/move-file/', {
          fileId: idStr,
          clientId,
          folder_id: folderId,
        });
        return;
      }
      await api.post(`/api/multi-tenant/clients/${clientId}/files/${fileId}/move/`, {
        folder_id: folderId,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clientFilesKeys.folders(clientId) });
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'clientFileContents' && q.queryKey[1] === clientId });
      onToast?.('File moved', 'success');
    },
    onError: (err) => {
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Move failed';
      onToast?.(msg, 'error');
    },
  });
}

export function useCreateFolderMutation(clientId, onToast) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, parentFolder }) => {
      const { data } = await api.post(`/api/multi-tenant/clients/${clientId}/folders/`, {
        name,
        parent_folder: parentFolder || null,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clientFilesKeys.folders(clientId) });
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'clientFileContents' && q.queryKey[1] === clientId });
      onToast?.('Folder created', 'success');
    },
    onError: (err) => {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.response?.data?.message || 'Could not create folder';
      onToast?.(msg, 'error');
    },
  });
}

export function useUploadDocumentsMutation(clientId, folderId, onToast) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ files, onProgress }) => {
      const total = files.length;
      for (let i = 0; i < total; i += 1) {
        const f = files[i];
        const form = new FormData();
        form.append('file', f);
        const url = folderId
          ? `/api/multi-tenant/clients/${clientId}/folders/${folderId}/files/`
          : `/api/multi-tenant/clients/${clientId}/files/`;
        await api.post(url, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt) => {
            if (evt.total && onProgress) {
              const pct = Math.round(((i + evt.loaded / evt.total) / total) * 100);
              onProgress(pct, f.name);
            }
          },
        });
      }
    },
    onSuccess: (_, { files }) => {
      qc.invalidateQueries({ queryKey: clientFilesKeys.folders(clientId) });
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'clientFileContents' && q.queryKey[1] === clientId });
      onToast?.(`${files.length} file${files.length > 1 ? 's' : ''} uploaded`, 'success');
    },
    onError: (err) => {
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Upload failed';
      onToast?.(msg, 'error');
    },
  });
}
